import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PasteFromWebModal from "@/components/PasteFromWebModal";
import RichEditor from "@/components/RichEditor";
import {
  ArrowLeft, ArrowRight, CheckCircle, Circle, Sparkle, FloppyDisk,
  Eye, TrashSimple, MagnifyingGlass, MagicWand,
} from "@phosphor-icons/react";

/**
 * FarmersCropArticlesIndex — /admin/farmers/crops/articles
 * Admin dashboard listing every crop with an "article published" flag.
 * Click a crop → paste editor.
 */
export function FarmersCropArticlesIndex() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | missing | published

  useEffect(() => {
    http.get("/farmers/crops/articles/index")
      .then(({ data }) => setRows(data.crops || []))
      .catch(() => toast.error("Unable to load the article index (are you an admin?)"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows
      .filter((r) => {
        if (filter === "missing" && r.has_article) return false;
        if (filter === "published" && !r.has_article) return false;
        if (!needle) return true;
        return (r.common_name || "").toLowerCase().includes(needle) ||
               (r.slug || "").toLowerCase().includes(needle);
      });
  }, [rows, q, filter]);

  const published = rows.filter((r) => r.has_article).length;

  return (
    <main
      className="bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332] min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10"
      data-testid="crop-articles-index"
    >
      <div className="max-w-[1100px] mx-auto">
        <Link
          to="/farmers"
          className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]"
        >
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <div className="mt-3 flex items-baseline gap-4 flex-wrap">
          <h1 className="font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
            Crop articles
          </h1>
          <span
            className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]"
            data-testid="crop-articles-stats"
          >
            {published} / {rows.length} published
          </span>
        </div>
        <p className="mt-2 max-w-2xl text-sm text-[#3A3830]">
          Paste an elaborate article per crop. Farmers Pulse renders it in
          full editorial layout on <code>/farmers/crops/&lt;slug&gt;</code>.
          Markdown headings, lists and links are supported.
        </p>

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 rounded-lg border border-black/15 bg-white px-3 py-2 w-72 max-w-full">
            <MagnifyingGlass size={13} weight="bold" className="text-[#3A3830]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search crops…"
              data-testid="crop-articles-search"
              className="flex-1 bg-transparent text-sm outline-none"
            />
          </div>
          {[
            { key: "all",       label: `All (${rows.length})` },
            { key: "missing",   label: `Missing (${rows.length - published})` },
            { key: "published", label: `Published (${published})` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`crop-articles-filter-${f.key}`}
              className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase ${
                filter === f.key
                  ? "bg-[#241209] text-[#F5EBDC] border-[#241209]"
                  : "bg-white border-black/15 hover:border-black/40"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-16 rounded-2xl bg-white/60 animate-pulse" />
            ))}
          </div>
        ) : (
          <ul
            className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3"
            data-testid="crop-articles-list"
          >
            {filtered.map((r) => (
              <li key={r.slug}>
                <Link
                  to={`/admin/farmers/crops/${r.slug}/article`}
                  data-testid={`crop-article-row-${r.slug}`}
                  className="flex items-center gap-3 rounded-2xl border border-black/10 bg-white p-3 hover:border-[color:var(--np-brand,#B85F2A)]"
                >
                  <div className="text-3xl shrink-0">{r.emoji || "🌱"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-display text-lg leading-tight truncate">
                      {r.common_name}
                    </div>
                    <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">
                      {r.category} · {r.slug}
                    </div>
                    {r.has_article && (
                      <div className="mt-0.5 text-xs text-[#3A3830] truncate">
                        {r.article_title} · {r.article_word_count} words
                      </div>
                    )}
                  </div>
                  {r.has_article ? (
                    <CheckCircle
                      size={20}
                      weight="fill"
                      className="text-emerald-600 shrink-0"
                    />
                  ) : (
                    <Circle size={20} weight="bold" className="text-black/25 shrink-0" />
                  )}
                  <ArrowRight size={14} weight="bold" className="text-[#3A3830] shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}


/**
 * FarmersCropArticleEditor — /admin/farmers/crops/:slug/article
 * Paste-and-publish editor for a single crop's article.
 */
export function FarmersCropArticleEditor() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [crop, setCrop] = useState(null);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [heroFactsText, setHeroFactsText] = useState("");
  const [furtherText, setFurtherText] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [shortHtml, setShortHtml] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");

  useEffect(() => {
    http.get(`/farmers/crops/${slug}`)
      .then(({ data }) => setCrop(data.crop))
      .catch(() => toast.error("Crop not found"));
    http.get(`/farmers/crops/${slug}/article`)
      .then(({ data }) => {
        const a = data.article;
        if (a) {
          setTitle(a.title || "");
          setSubtitle(a.subtitle || "");
          setShortHtml(a.short_html || "");
          setBodyHtml(a.body_html || (a.body_md ? "" : ""));
          setBody(a.body_md || "");
          setHeroFactsText((a.hero_facts || []).join("\n"));
          setFurtherText((a.further_reading || []).join("\n"));
        }
      })
      .catch(() => { /* 404 = no article yet, that's fine */ })
      .finally(() => setLoaded(true));
  }, [slug]);

  const wordCount = useMemo(() => {
    const text = (bodyHtml || "").replace(/<[^>]+>/g, " ") + " " + (body || "");
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  }, [body, bodyHtml]);

  const save = async () => {
    if (title.trim().length < 3) return toast.error("Title is too short");
    const longLen = (bodyHtml || "").replace(/<[^>]+>/g, "").trim().length + (body || "").trim().length;
    if (longLen < 20) return toast.error("Long article body is too short");
    setSaving(true);
    try {
      const payload = {
        title:    title.trim(),
        subtitle: subtitle.trim() || null,
        short_html: shortHtml || null,
        body_html:  bodyHtml || null,
        body_md:    body || null,
        hero_facts:      heroFactsText.split("\n").map((s) => s.trim()).filter(Boolean),
        further_reading: furtherText.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      await http.put(`/farmers/crops/${slug}/article`, payload);
      toast.success("Article published");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed — are you an admin?");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this article? The crop page will fall back to the stats-only layout.")) return;
    try {
      await http.delete(`/farmers/crops/${slug}/article`);
      toast.success("Article deleted");
      setTitle(""); setSubtitle(""); setBody(""); setBodyHtml(""); setShortHtml("");
      setHeroFactsText(""); setFurtherText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <main
      className="bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332] min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10"
      data-testid="crop-article-editor"
    >
      <div className="max-w-[1200px] mx-auto">
        <Link
          to="/admin/farmers/crops/articles"
          className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]"
        >
          <ArrowLeft size={11} weight="bold" /> Crop articles
        </Link>

        <header className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              <Sparkle size={11} weight="fill" className="inline mr-1" />
              Paste editor
            </div>
            <h1 className="mt-1 font-serif-display text-3xl md:text-4xl leading-[1.02] tracking-tight">
              {crop?.emoji || "🌱"} {crop?.common_name || slug}
            </h1>
            {crop?.scientific_name && (
              <p className="mt-1 text-sm italic text-[#3A3830]">
                {crop.scientific_name}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setPasteOpen(true)}
              data-testid="crop-article-paste-web"
              className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
            >
              <MagicWand size={11} weight="fill" /> Paste from web
            </button>
            <button
              onClick={() => setPreview((p) => !p)}
              data-testid="crop-article-preview-toggle"
              className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
            >
              <Eye size={11} weight="fill" /> {preview ? "Edit" : "Preview"}
            </button>
            <Link
              to={`/farmers/crops/${slug}`}
              target="_blank"
              rel="noreferrer"
              data-testid="crop-article-open-live"
              className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
            >
              Open live page <ArrowRight size={11} weight="bold" />
            </Link>
            <button
              onClick={remove}
              data-testid="crop-article-delete"
              className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 text-red-800 px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-red-100"
            >
              <TrashSimple size={11} weight="fill" /> Delete
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="crop-article-save"
              className="inline-flex items-center gap-1 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50"
            >
              <FloppyDisk size={11} weight="fill" /> {saving ? "Saving…" : "Publish"}
            </button>
          </div>
        </header>

        {!loaded ? (
          <div className="mt-6 h-96 rounded-2xl bg-white/60 animate-pulse" />
        ) : preview ? (
          <ArticlePreview
            title={title}
            subtitle={subtitle}
            shortHtml={shortHtml}
            bodyHtml={bodyHtml}
            body={body}
            heroFacts={heroFactsText.split("\n").map((s) => s.trim()).filter(Boolean)}
            further={furtherText.split("\n").map((s) => s.trim()).filter(Boolean)}
          />
        ) : (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-5">
            <div className="rounded-2xl border border-black/10 bg-white p-4 md:p-5">
              <label className="block text-xs font-mono-tech tracking-[0.28em] uppercase text-[#3A3830]">
                Title
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="crop-article-title-input"
                placeholder="e.g. Maize — the golden grain that fed a civilisation"
                className="mt-1 w-full bg-transparent border-b border-black/15 py-2 font-serif-display text-2xl outline-none focus:border-black/50"
              />

              <label className="mt-4 block text-xs font-mono-tech tracking-[0.28em] uppercase text-[#3A3830]">
                Subtitle
              </label>
              <input
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                data-testid="crop-article-subtitle-input"
                placeholder="One-line hook (optional)"
                className="mt-1 w-full bg-transparent border-b border-black/15 py-2 text-sm italic outline-none focus:border-black/50"
              />

              <label className="mt-6 block text-xs font-mono-tech tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                Short teaser <span className="normal-case text-[10px] text-[#3A3830]/70">(1-3 sentences, shown on the crops list card + top of detail page)</span>
              </label>
              <div className="mt-1" data-testid="crop-article-short-editor">
                <RichEditor
                  value={shortHtml}
                  onChange={setShortHtml}
                  placeholder="A short, punchy summary that will appear on the /farmers/crops list card…"
                  testid="crop-short"
                />
              </div>

              <label className="mt-6 block text-xs font-mono-tech tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                Long article <span className="normal-case text-[10px] text-[#3A3830]/70">(full editorial — images, YouTube, podcast links, everything)</span>
              </label>
              <div className="mt-1" data-testid="crop-article-body-editor">
                <RichEditor
                  value={bodyHtml || (body ? `<p>${body.split("\n\n").join("</p><p>")}</p>` : "")}
                  onChange={setBodyHtml}
                  placeholder="Origins & history, how it grows, climate, pests, nutrition, culture, future…"
                  testid="crop-body"
                />
              </div>

              <div className="mt-2 flex items-center justify-between font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#3A3830]">
                <span>{wordCount} words</span>
                <span>Rich editor + paste-from-web</span>
              </div>
            </div>

            <aside className="space-y-4">
              <div className="rounded-2xl border border-black/10 bg-white p-4">
                <label className="block text-xs font-mono-tech tracking-[0.28em] uppercase text-[#3A3830]">
                  Hero facts <span className="normal-case text-[10px] text-[#3A3830]/70">(one per line, max 6)</span>
                </label>
                <textarea
                  value={heroFactsText}
                  onChange={(e) => setHeroFactsText(e.target.value)}
                  data-testid="crop-article-hero-input"
                  placeholder={"3rd most grown grain on Earth\n10,000 years of cultivation\n1.1 billion tonnes / year"}
                  className="mt-1 w-full h-32 rounded-lg border border-black/15 bg-[#FBF2E1]/40 p-2 text-sm outline-none focus:border-black/40 resize-y"
                />
              </div>

              <div className="rounded-2xl border border-black/10 bg-white p-4">
                <label className="block text-xs font-mono-tech tracking-[0.28em] uppercase text-[#3A3830]">
                  Further reading <span className="normal-case text-[10px] text-[#3A3830]/70">(one per line)</span>
                </label>
                <textarea
                  value={furtherText}
                  onChange={(e) => setFurtherText(e.target.value)}
                  data-testid="crop-article-further-input"
                  placeholder={"FAO country crop briefs — https://www.fao.org/faostat/\nCGIAR — maize mandate"}
                  className="mt-1 w-full h-32 rounded-lg border border-black/15 bg-[#FBF2E1]/40 p-2 text-sm outline-none focus:border-black/40 resize-y"
                />
              </div>

              <div className="rounded-2xl border border-black/10 bg-[#FBF2E1] p-4 text-xs text-[#3A3830]">
                <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                  Tips
                </div>
                <ul className="mt-1 list-disc pl-4 space-y-1">
                  <li>Use <code>##</code> for section headings.</li>
                  <li>Paste web-copied articles directly — most markdown / rich-text formatting is preserved.</li>
                  <li>Preview anytime — nothing is public until you click <strong>Publish</strong>.</li>
                </ul>
              </div>
            </aside>
          </div>
        )}
      </div>

      <PasteFromWebModal
        open={pasteOpen}
        onClose={() => setPasteOpen(false)}
        onApply={(md, mode) => {
          // Convert cleaned markdown → simple HTML the RichEditor can render.
          const html = md
            .replace(/^### (.*)$/gm, "<h3>$1</h3>")
            .replace(/^## (.*)$/gm, "<h2>$1</h2>")
            .replace(/^# (.*)$/gm, "<h1>$1</h1>")
            .replace(/^> (.*)$/gm, "<blockquote><p>$1</p></blockquote>")
            .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
            .replace(/_(.+?)_/g, "<em>$1</em>")
            .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>')
            .split(/\n{2,}/).map((p) => p.startsWith("<") ? p : `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("\n");
          setBodyHtml((prev) =>
            mode === "append" && (prev || "").trim()
              ? `${prev}\n${html}`
              : html,
          );
          toast.success(mode === "append" ? "Appended cleaned content" : "Body replaced with cleaned content");
        }}
      />
    </main>
  );
}


function ArticlePreview({ title, subtitle, shortHtml, bodyHtml, body, heroFacts, further }) {
  return (
    <article
      className="mt-6 rounded-2xl border border-black/10 bg-white p-6 md:p-10"
      data-testid="crop-article-preview"
    >
      <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
        Farmers Pulse editorial · preview
      </div>
      <h2 className="mt-1 font-serif-display text-3xl md:text-4xl leading-[1.05] tracking-tight">
        {title || "Untitled article"}
      </h2>
      {subtitle && (
        <p className="mt-2 font-serif-display italic text-lg text-[#3A3830] max-w-2xl">
          {subtitle}
        </p>
      )}
      {shortHtml && (
        <div
          className="mt-4 rounded-2xl bg-[#FBF2E1] p-4 crop-article-body max-w-3xl"
          dangerouslySetInnerHTML={{ __html: shortHtml }}
        />
      )}
      {heroFacts.length > 0 && (
        <ul className="mt-5 grid sm:grid-cols-3 gap-3">
          {heroFacts.slice(0, 6).map((f, i) => (
            <li key={i} className="rounded-2xl border border-black/10 bg-[#FBF2E1] p-3 text-sm">
              <Sparkle size={11} weight="fill" className="inline mr-1 text-[color:var(--np-brand,#B85F2A)]" />
              {f}
            </li>
          ))}
        </ul>
      )}
      {bodyHtml ? (
        <div
          className="mt-6 max-w-3xl font-serif-display text-[17px] leading-[1.75] text-[#241209] crop-article-body"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      ) : (
        <div className="mt-6 max-w-3xl font-serif-display text-[17px] leading-[1.75] text-[#241209] crop-article-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{body || "*Nothing to preview yet.*"}</ReactMarkdown>
        </div>
      )}
      {further.length > 0 && (
        <section className="mt-8 border-t border-black/10 pt-4">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Further reading
          </div>
          <ul className="mt-2 space-y-1 text-sm text-[#3A3830]">
            {further.map((r, i) => (
              <li key={i} className="pl-3 border-l-2 border-black/10">
                {/^https?:\/\//i.test(r) ? (
                  <a href={r} target="_blank" rel="noreferrer" className="underline text-[color:var(--np-brand,#B85F2A)]">
                    {r}
                  </a>
                ) : r}
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}
